/**
 * The frozen vocabulary of docs/CONTRACT.md — error envelope (§2), views (§6) and every REST
 * request/response body (§4). Runtime-free: types only, no imports.
 */

/* ------------------------------------------------------------------ errors */

export type ApiErrorCode =
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "bad_request"
  | "rate_limited"
  | "too_large"
  | "storage_full"
  | "conflict";

export type ApiError = { code: ApiErrorCode; message: string };

/** The §2 envelope; most non-2xx `/api/*` responses carry it. */
export type ApiErrorResponse = { error: ApiError };

/**
 * §4/§11: sign-up, login and the approval gate answer with a flat `error` code instead of the
 * envelope, so the client can branch on the code without unwrapping an object.
 */
export type AuthFailureCode = "email_taken" | "bad_credentials";
export type AuthFailureResponse = { error: AuthFailureCode };

/** §11: the gate's 403 — the code, plus the caller's own status for the waiting screen. */
export type NotApprovedResponse = { error: "not_approved"; status: UserStatus };

/* ------------------------------------------------------------------- enums */

export type ChatKind = "dm" | "group";
export type CallKind = "audio" | "video";
export type MediaKind = "image" | "video" | "file" | "voice";
export type MessageKind = "text" | "image" | "video" | "file" | "voice" | "call" | "system" | "poll";
export type LiveKitStatus = "up" | "down";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "OPTIONS";

/** §11: `pending` waits for an admin, `approved` is the only status that opens the API. */
export type UserStatus = "pending" | "approved" | "rejected" | "suspended";

/* ------------------------------------------------------------------- views */

export type UserView = {
  id: string;
  handle: string;
  displayName: string;
  /** `/api/media/<id>/thumb` or null. */
  avatarUrl: string | null;
  online: boolean;
  lastSeenAt: number | null;
  /** §11: the gate reads this; a client branches on it to show the app or the waiting screen. */
  status: UserStatus;
  /** §6: admin affordances in the UI. Never means the caller may skip the gate. */
  isAdmin: boolean;
};

export type MediaView = {
  id: string;
  kind: MediaKind;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** Signed `/api/media/<id>?exp=…&sig=…` (A17) — fetchable without a header. */
  url: string;
  /** The `/thumb` twin, signed the same way, or null when thumbnail generation failed. */
  thumbUrl: string | null;
};

/**
 * W9: the fixed reaction set — no vendor, six that a family actually uses. Order is the picker's
 * order and the view's order, so both clients and the api agree without a second table.
 */
export const REACTION_EMOJI = ["👍", "❤️", "😂", "😮", "😢", "🙏"] as const;
export type ReactionEmoji = (typeof REACTION_EMOJI)[number];

/** Narrowing for anything that arrives as a string — a request body, a stored row. */
export function isReactionEmoji(value: string): value is ReactionEmoji {
  return (REACTION_EMOJI as readonly string[]).includes(value);
}

/** W11: one option of a poll, with the members who chose it (the caller's own is `voterIds.includes(me)`). */
export type PollOptionView = { index: number; text: string; voterIds: string[] };

/** W11: a poll message's payload — the question is the message body. */
export type PollView = { options: PollOptionView[]; multiple: boolean };

/** `PUT /api/messages/:id/vote` — the caller's vote on one option (toggles). */
export type VoteRequest = { optionIndex: number };

/** One emoji's worth of reactions on a message: who used it, in the order they did. */
export type MessageReactionView = { emoji: string; userIds: string[] };

export type MessageView = {
  id: string;
  chatId: string;
  authorId: string;
  author: UserView | null;
  kind: MessageKind;
  body: string | null;
  media: MediaView | null;
  replyToId: string | null;
  callId: string | null;
  editedAt: number | null;
  deletedAt: number | null;
  /** True on a copy made by forwarding (W4); the original never carries the mark. */
  forwarded: boolean;
  /** W9: one entry per emoji anyone used; the caller's own reaction is `userIds.includes(me)`. */
  reactions: MessageReactionView[];
  /** W10: the members this message @-mentions, by id — the body carries the `@handle` text. */
  mentions: string[];
  /** W11: the poll this message is, or null — a `poll` kind always carries one. */
  poll: PollView | null;
  createdAt: number;
  /** Present when the message was created by a client that supplied one. */
  clientId?: string;
  /** Client-side only: true while an optimistic message is not confirmed. */
  pending?: boolean;
};

/** One member's read cursor (docs/CONTRACT.md §6). Ids are UUIDv7, so `messageId` order is time order. */
export type ChatReadView = {
  userId: string;
  messageId: string;
};

export type ChatView = {
  id: string;
  kind: ChatKind;
  title: string | null;
  avatarUrl: string | null;
  memberCount: number;
  members: UserView[];
  lastMessage: MessageView | null;
  unreadCount: number;
  /** The *viewer's* own cursor. */
  lastReadId: string | null;
  /** The viewer's mute expiration in epoch ms, -1 for forever, or null if unmuted / expired. */
  mutedUntil: number | null;
  /** The *viewer's* own pin (W5): epoch ms, or null when this chat is not pinned for them. */
  pinnedAt: number | null;
  /** The chat's one pinned message (W5), or null. A tombstone never appears here. */
  pinnedMessage: MessageView | null;
  /** W13: the chat's disappearing-message timer in seconds, or null for off (the default). */
  disappearingSeconds: number | null;
  /**
   * Every member's cursor that is set, the viewer's included (the viewer's own row equals
   * `lastReadId`). Live `read` frames are otherwise the only source, so this is what seeds a
   * peer cursor after a relaunch instead of waiting for the next frame.
   */
  reads: ChatReadView[];
  lastMessageAt: number | null;
  /** `activeCall !== null`, kept as its own field for chat lists that only need the flag. */
  hasActiveCall: boolean;
  /**
   * The running call (the one `POST /api/chats/:id/calls` created and that has not been ended), or
   * null. Carrying it here is what lets "join the running call" act without a second request.
   */
  activeCall: ChatCallView | null;
};

export type CallView = {
  id: string;
  chatId: string;
  roomName: string;
  kind: CallKind;
  startedBy: string;
  startedAt: number;
  endedAt: number | null;
  /** W6: when someone other than the caller first joined, or null — the call was never answered. */
  answeredAt: number | null;
  /** W6: 'ended' (a member hung up) or 'reaped' (the sweep found the room empty); null if live. */
  endedReason: string | null;
  /** Live participants, read from the SFU; [] once the room is gone. */
  participants: UserView[];
};

/**
 * The call as a `ChatView` carries it: `CallView` without `participants`, because who is in the
 * room is an SFU round trip and a chat view must stay one database query. `GET /api/calls/:id` or
 * `GET /api/chats/:id/calls` has the live list.
 */
export type ChatCallView = Omit<CallView, "participants">;

/** One row of `GET /api/calls` (W6): the call, the chat it happened in, and what it was. */
export type CallLogView = {
  call: ChatCallView;
  chat: ChatView;
  /** Relative to the viewer. */
  direction: "outgoing" | "incoming";
  /** Ended with nobody but the caller ever in the room — a decline counts as missed. */
  missed: boolean;
  /** `endedAt - answeredAt` for a call that was answered, else null. */
  durationMs: number | null;
};

export type CallLogResponse = { calls: CallLogView[] };

export type DeviceView = {
  id: string;
  name: string;
  platform: string | null;
  createdAt: number;
  lastSeenAt: number | null;
};

export type InviteView = {
  id: string;
  code: string;
  label: string | null;
  isAdmin: boolean;
  maxUses: number;
  uses: number;
  createdAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
  /** A2 recovery: the user this code attaches a device to, or null for an ordinary invite. */
  userId: string | null;
};

export type AdminMemberView = {
  user: UserView;
  deviceCount: number;
  online: boolean;
};

export type AdminStatsView = {
  users: number;
  chats: number;
  messages: number;
  mediaBytes: number;
  diskFreeBytes: number;
};

export type HealthView = {
  ok: true;
  version: string;
  livekit: LiveKitStatus;
  startedAt: number;
};

/* ----------------------------------------------------------------- payloads */

export type OkResponse = { ok: true };

/* auth + me */

export type InviteAuthRequest = {
  code: string;
  /** Required to create a new identity; ignored when the code attaches a device to an existing user. */
  displayName?: string;
  /**
   * §4: written on the user the code lands on — the created user for an unbound invite, the bound
   * user for a recovery code (an admin-initiated reset). Same validation as sign-up.
   */
  email?: string;
  password?: string;
  deviceName?: string;
  platform?: string;
};

/** §4: open sign-up, landing the new user on `status:"pending"` with a device token already minted. */
export type SignupRequest = {
  /** A plausible address, stored lower-cased and unique. */
  email: string;
  /** 8–200 chars; argon2id. */
  password: string;
  /** 1–40 chars; the handle is derived from it and uniquified. */
  displayName: string;
  deviceName?: string;
  platform?: string;
};

/** §4: an existing user adds a device. Unknown email and wrong password are both `bad_credentials`. */
export type LoginRequest = {
  email: string;
  password: string;
  deviceName?: string;
  platform?: string;
};

export type AuthResponse = {
  token: string;
  user: UserView;
  /** The `devices` row this token belongs to — what a settings screen marks "This device". */
  deviceId: string;
};

export type MeResponse = {
  user: UserView;
  devices: DeviceView[];
  /** The caller's own row in `devices`, so a client never has to guess which one it is. */
  deviceId: string;
  /**
   * W13: what this member shares. Only `/api/me` (and the privacy patch) carries it — it is the
   * member's own policy, and a client needs it to render the switches honestly.
   */
  privacy: { shareLastSeen: boolean; shareReadReceipts: boolean };
};
export type UserResponse = { user: UserView };

export type PatchMeRequest = {
  displayName?: string;
  /** A media row with scope "avatar" owned by the caller, or null to clear. */
  avatarMediaId?: string | null;
};

/**
 * §4: `PATCH /api/me/password` — the caller changes their own password. The current one is proved
 * against the stored argon2id hash, so a wrong one is `401 bad_credentials` (flat body), the same
 * answer login gives. `newPassword` is 8–200 chars, like every other password write.
 */
export type ChangePasswordRequest = {
  currentPassword: string;
  newPassword: string;
};

export type DevicesResponse = { devices: DeviceView[]; deviceId: string };

export type WebPushKeys = { p256dh: string; auth: string };

export type WebPushSubscribeRequest = {
  kind: "webpush";
  endpoint: string;
  keys: WebPushKeys;
  ua?: string;
};

export type FcmSubscribeRequest = {
  kind: "fcm";
  fcmToken: string;
  ua?: string;
};

export type PushSubscribeRequest = WebPushSubscribeRequest | FcmSubscribeRequest;
export type PushUnsubscribeRequest = { endpoint: string };

/* directory */

/** `GET /api/users` — every member ordered by displayName, the caller included. */
export type UsersResponse = { users: UserView[] };

/* admin */

export type InvitesResponse = { invites: InviteView[] };

export type CreateInviteRequest = {
  label?: string;
  isAdmin?: boolean;
  maxUses?: number;
  expiresInHours?: number;
};

export type CreateInviteResponse = { invite: InviteView; url: string };

/** `POST /api/admin/members/:id/recovery` — a single-use, expiring code bound to one user. */
export type CreateRecoveryRequest = { expiresInHours?: number };
export type CreateRecoveryResponse = { invite: InviteView; url: string };
export type AdminMembersResponse = { members: AdminMemberView[] };

/**
 * §6: the admin console's row — `UserView` plus the account's email. Admin-only on purpose: the
 * directory every member can read must never carry addresses, and the console's job is judging
 * strangers.
 */
export type AdminUserView = UserView & { email: string | null };

/** §4: every user, optionally filtered by status — the console's pending queue. */
export type AdminUsersResponse = { users: AdminUserView[] };

/**
 * §4: `PATCH /api/admin/users/:id` — approve, promote or provision in one route. The api refuses a
 * change to the caller's own `status` or `isAdmin` (any other field on the caller's own row is fine).
 */
export type AdminUserPatchRequest = {
  status?: UserStatus;
  isAdmin?: boolean;
  /** Written lower-cased; a duplicate is `409 email_taken`. */
  email?: string;
  /** 8–200 chars; argon2id. */
  password?: string;
};

/* chats */

export type ChatsResponse = { chats: ChatView[] };
export type ChatResponse = { chat: ChatView };
export type ChatDetailResponse = { chat: ChatView; members: UserView[] };

export type CreateChatRequest = {
  kind: ChatKind;
  /** The other members; the caller is always included. */
  memberIds: string[];
  title?: string;
};

export type PatchChatRequest = {
  title?: string | null;
  avatarMediaId?: string | null;
};

export type PatchChatMuteRequest = {
  until: number | null;
};

export type AddChatMembersRequest = { userIds: string[] };
export type AddChatMembersResponse = { chat: ChatView; members: UserView[] };

export type MessagesQuery = { before?: string; limit?: number };

export type MessagesResponse = {
  /** Chronological (oldest first) within the returned window. */
  messages: MessageView[];
  hasMore: boolean;
};

export type PostMessageRequest = {
  kind: MessageKind;
  body?: string | null;
  mediaId?: string | null;
  clientId: string;
  replyToId?: string | null;
  /** W10: the members this message @-mentions, by id — the body carries the `@handle` text. */
  mentions?: string[];
  /** W11: required for `kind: "poll"` — the options; the question is the message body. */
  poll?: { options: string[]; multiple?: boolean };
};

export type MessageResponse = { message: MessageView };

/** `PATCH /api/messages/:id` — an edit rewrites the body in place and sets `editedAt` (§4). */
export type EditMessageRequest = { body: string };

/** `POST /api/chats/:id/export` — the archive a member asked for (W14). */
export type ExportView = {
  id: string;
  /** A signed, expiring link: a browser can fetch it with no bearer token. */
  url: string;
  bytes: number;
  messageCount: number;
  mediaCount: number;
  createdAt: number;
};
export type ExportResponse = { export: ExportView };

/** `PATCH /api/me/privacy` — what the caller shares (W13). Both fields are optional. */
export type PrivacyRequest = { shareLastSeen?: boolean; shareReadReceipts?: boolean };

/** `PATCH /api/chats/:id/disappearing` — the chat's timer in seconds, or null for off (W13). */
export type DisappearingRequest = { seconds: number | null };

/** `PUT /api/messages/:id/reaction` — the caller's one reaction on that message (§4, W9). */
export type SetReactionRequest = { emoji: string };

/** `POST /api/messages/forward` — one copy per (message, chat) pair, authored by the forwarder. */
export type ForwardMessagesRequest = { messageIds: string[]; chatIds: string[] };
export type ForwardMessagesResponse = { messages: MessageView[] };

/** One search result: the message and the chat it lives in, so a global hit renders as a row. */
export type SearchHit = { message: MessageView; chat: ChatView };

export type SearchResponse = {
  /** Echoes the trimmed query, so a client can drop a response that arrived out of order. */
  query: string;
  hits: SearchHit[];
  hasMore: boolean;
};

export type SearchQuery = {
  q: string;
  /** Restricts the search to one chat (the thread's own field). */
  chatId?: string;
  limit?: number;
};
export type MediaResponse = { media: MediaView };
export type ReadRequest = { messageId: string };
export type CallsResponse = { calls: CallView[] };

/* calls */

export type StartCallRequest = { kind: CallKind };

/**
 * Ringing *additional* members into a live call (`POST /api/calls/:callId/ring`, CONTRACT §4/§9).
 * The chat's membership is the room, so a member can always `joinCall` once they know the call is
 * running; this is the missing half — the ring that tells the chosen members it is. `userIds` must
 * name members of the call's chat other than the caller.
 */
export type RingCallRequest = { userIds: string[] };

export type CallTokenResponse = { call: CallView; token: string; wsUrl: string };
export type CallDetailResponse = { call: CallView; participants: UserView[] };
